import api from '@/api';
import store from '@/bootstrap/vuex';
import router from '@/bootstrap/router';
import { isPortalOnly } from '@/utilities/portal-only';

/*
 * `errors.forbidden` lives at `/:organisation/errors/403`, so a redirect to it
 * must carry the organisation param EXPLICITLY. Inside a guard, vue-router
 * fills missing params from the CURRENT location — which during the initial
 * navigation of a fresh page-load is the START location `/` with no params at
 * all. The bare `next({ name: 'errors.forbidden' })` therefore threw
 * `Missing required param "organisation"` while resolving, the initial
 * navigation aborted, and the app mounted with no matched route: a completely
 * blank page at the typed URL, with a clean console in the production build
 * (the router only `warn`s, and warns are stripped from prod). `__my__` is the
 * slug fallback the root catch-all in `routes/shared/error.js` already uses.
 */
const forbiddenRedirect = (to) => ({
    name: 'errors.forbidden',
    params: { organisation: to.params.organisation ?? '__my__' },
});

/*
 * D-06: the more specific refusal destination for the portal-only class. A
 * customer whose map in the target organisation holds only `access.client-
 * portal.*` grants (see `@/utilities/portal-only`) is refused EVERY app-module
 * screen there, so the forbidden page tells them nothing actionable — their
 * portal home does. It sits on the refusal path only, after the permission
 * check has already failed, and carries the organisation param explicitly for
 * the same START-location reason `forbiddenRedirect` does. `client-portal.
 * start` resolves during matching to `client-portal.tasks`, whose meta the
 * portal-only class by definition passes — so the bounce cannot loop.
 *
 * D-06 is a derived decision (plan Q-03) with its veto window open: if vetoed,
 * both call sites revert to `next(forbiddenRedirect(to))`.
 */
const portalHomeRedirect = (to) => ({
    name: 'client-portal.start',
    params: { organisation: to.params.organisation ?? '__my__' },
});

const nonTenantRoutes = [
    'app.home',
    'app.profile',
    'app.profile.update',
    'tenant.register',
    'tenant.playground.create',
    'tenant.playground.start-over',
    'app.whatsnew',
    'app.introduction',
    'tenant.accept-invitation',
    'received-invoices.view-invoice',
    'practice-management.prospect-profile',
    'client-portal.accept-invitation',
];

const beforeEach = (to, from, next) => {
    // console.log('beforeEach', to, from);
    // console.log('to.params.organisation', to.params.organisation);

    // check that only guests can access guest routes
    if (to.meta.guestRequired && to.meta.guestRequired == true && Object.keys(store.state.app.user).length == 0) {
        // console.log('Logged out guestRequired');
        return next();
    }

    if (to.meta.guestRequired && to.meta.guestRequired == true && Object.keys(store.state.app.user).length > 0) {
        // console.log('Logged in guest take them to the menu', to.name);
        return next({ name: 'app.home' });
    }

    // check that only logged in users can access logged in routes
    if (Object.keys(store.state.app.user).length > 0) {
        // console.log('Attempt to navigate to a logged required route');
        if (to.meta.permission == undefined) {
            alert(to.name + ' does not have an associated permission!');
            throw to.name + ' does not have an associated permission!';
        }

        // console.log('org::param', to.params.organisation);
        // console.log('org::app', store.state.app);
        // console.log('org::store', store.state.organisation);
        // console.log('org::length', store.state.organisation.length);
        // console.log('org::self', store.state.organisation[to.params.organisation]);

        if (store.state.organisation[to.params.organisation] == undefined && !nonTenantRoutes.includes(to.name)) {
            // console.log('No organisation set and not a nonTenantRoute', to.name);
            return next({ name: 'app.home' });
        }

        if (to.meta.permission == '*') {
            // console.log('Everyone can access this route', to.name);
            return next();
        }

        if (store.state.organisation[to.params.organisation].permissions[to.meta.permission] != undefined && store.state.organisation[to.params.organisation].permissions[to.meta.permission]) {
            // console.log('User has permission to access this route', to.name);
            return next();
        }

        if (isPortalOnly(store.state.organisation[to.params.organisation]?.permissions)) {
            return next(portalHomeRedirect(to));
        }

        next(forbiddenRedirect(to));
    } else {
        // console.log('Attempt to navigate and not logged in');
        if (to.meta.loginRequired && to.meta.loginRequired == true && to.name != 'guest.login') {
            // console.log('Login required', to.name);
            api.get('/v1/sessions')
                .then((response) => {
                    store.commit('app/setUser', response.data.user);

                    if (to.meta.permission == undefined) {
                        alert(to.name + ' does not have an associated permission!');
                        throw to.name + ' does not have an associated permission!';
                    }

                    if (store.state.organisation[to.params.organisation] == undefined && !nonTenantRoutes.includes(to.name)) {
                        // console.log('Session: (direct page load) no organisation set and not a nonTenantRoute', to.name);
                        return next({ name: 'app.home' });
                    }

                    if (to.meta.permission == '*') {
                        // console.log('Session: (direct page load) everyone can access this route', to.name);
                        return next();
                    }

                    if (store.state.organisation[to.params.organisation].permissions[to.meta.permission] != undefined && store.state.organisation[to.params.organisation].permissions[to.meta.permission]) {
                        // console.log('Session: (direct page load) user has permission to access this route', to.name);
                        return next();
                    }

                    if (isPortalOnly(store.state.organisation[to.params.organisation]?.permissions)) {
                        return next(portalHomeRedirect(to));
                    }

                    return next(forbiddenRedirect(to));
                })
                .catch((error) => {
                    // console.log('Session: (direct page load) no session found redirect to login', error);
                    // reset storage
                    localStorage.clear();
                    sessionStorage.clear();

                    sessionStorage.setItem('intended', JSON.stringify(to));
                    next({ name: 'guest.login' }, () => {});

                    return;
                });
        } else {
            if (to.meta.permission == undefined) {
                alert(to.name + ' does not have an associated permission!');
                throw to.name + ' does not have an associated permission!';
            }

            // console.log('No login required', to.name);

            next();
        }
    }
};

export default beforeEach;
